/**
 * Types for the core video feature (Chime SDK integration layer).
 * Business-logic types live in videoMeeting.
 */

// --- UI types ---

export type VideoUIActiveTab = "chat" | "roster" | "materials";

export type ContentShareZoom = {
  scale: number;
  translateX: number;
  translateY: number;
  isZoomed: boolean;
};

export type ScrollPositions = {
  [K in VideoUIActiveTab]: number;
};

// --- Config enums ---

export enum MeetingMode {
  Spectator,
  Attendee,
}

export enum Layout {
  Gallery,
  Featured,
}

export const VideoFiltersCpuUtilization = {
  Disabled: "0",
  CPU10Percent: "10",
  CPU20Percent: "20",
  CPU40Percent: "40",
} as const;

export const VideoTransformOptions = {
  None: "None",
  Blur: "Background Blur",
  Replacement: "Background Replacement",
} as const;

// --- Transcription types ---

export type TranscriptionSegment = {
  id: string;
  speakerId: string;
  speakerName: string;
  text: string;
  confidence: number;
  isPartial: boolean;
  startTime: number;
  endTime: number;
};

// --- Tile Grid types ---

/** Per-attendee visual state computed by useChimeObserver's state machine.
 *
 * `bandwidth_suppressed` is set when the SDK's downlink policy paused this
 * attendee's tile AND ChimeObserverState's `suppressionIntent` set marks
 * the pause as a deliberate suppression (severe/critical bandwidth tier),
 * as opposed to an incidental SDK-level pause which uses `frozen`. The
 * distinction is visual: `bandwidth_suppressed` renders avatar + a
 * "Limited video reception" pill; `frozen` keeps the last-decoded canvas
 * frame visible.
 *
 * `bandwidth_suppressed` is NOT subject to grace timers — recovery happens
 * through the SDK's `tileWillBeUnpausedByDownlinkPolicy` callback as a
 * direct transition back to 'video'. Local self-view tiles never enter
 * this state. */
export type TileState = 'video' | 'frozen' | 'avatar' | 'absent' | 'bandwidth_suppressed';

/**
 * Closed taxonomy of reasons a tile transitioned between TileStates.
 * Used as the `reason` argument when ChimeObserverState writes to
 * attendeeTileState — currently consumed only by the dev-only
 * `_debugTransition` console.debug helper, but kept as a closed union so
 * any code path that pattern-matches on the cause of a transition shares
 * the same discriminant shape.
 */
export type TileTransitionReason =
  | 'source_new'            // attendee first appears in the SDK source set or roster
  | 'source_lost'            // attendee dropped out of the SDK source list
  | 'source_reappeared'      // attendee returned to the source list; grace timer cancelled
  | 'mass_drop'              // ≥2 sources dropped in one batch, or total source loss
  | 'sdk_tile_update'        // videoTileDidUpdate triggered a state change
  | 'sdk_tile_removed'       // videoTileWasRemoved triggered a state change
  | 'grace_timer_fired'      // per-attendee grace timer expired; transition to 'avatar'
  | 'roster_drop'            // attendee left the roster (synchronous presence callback)
  | 'bandwidth_paused'       // VideoDownlinkObserver paused a tile
  | 'bandwidth_resumed'      // VideoDownlinkObserver unpaused a tile
  | 'connection_poor'        // connectionDidBecomePoor / SuggestStopVideo triggered a tile write
  | 'connection_good'        // connectionDidBecomeGood re-evaluation transitioned this tile
  | 'reconnect_guard'        // reconnect guard entered or cleared; tile re-evaluated
  | 'intent_camera_on'       // remote camera-on intent (data channel) transitioned the tile
  | 'intent_camera_off'      // remote camera-off intent (data channel) transitioned the tile
  | 'health_check_freeze'    // useVideoHealthCheck forced 'video' → 'frozen'
  | 'health_check_recovery'; // useVideoHealthCheck forced 'frozen' → 'video'

/** @deprecated Use TileState instead. Kept during migration. */
export type AttendeeVideoState = {
  attendeeId: string;
  name: string;
  videoEnabled: boolean;
  bandwidthConstrained: boolean;
};

export type VideoSourceState = {
  cameraSources: string[];
  activeSpeakersWithCameraSource: string[];
  contentShareId: string | null;
  hasLocalVideo: boolean;
  hasLocalContentSharing: boolean;
};

// --- Device intent (data channel) ---

export type AttendeeDeviceIntent = {
  cameraIntentOn: boolean;
  micIntentOn: boolean;
  lastUpdate: number;
};

// --- Connection quality ---

export type ConnectionQuality = 'good' | 'poor' | 'suggestStopVideo';

// --- Reactions ---

export type ReactionEvent = {
  id: string;
  emoji: string;
  attendeeId: string;
  timestamp: number;
};

// --- Events ---

export type VideoEvents = {
  "videochat:joined": { meetingId: string };
  "videochat:left": { meetingId: string };
  "videochat:muted": Record<string, never>;
  "videochat:unmuted": Record<string, never>;
  "videochat:camera:on": Record<string, never>;
  "videochat:camera:off": Record<string, never>;
  "videochat:audio:source:selected": { deviceId: string };
  "videochat:video:source:selected": { deviceId: string };
  "videochat:screen:shared": Record<string, never>;
  "videochat:screen:unshared": Record<string, never>;
  "videochat:participant:viewed": Record<string, never>;
  "videochat:materials:opened": Record<string, never>;
  "transcription:started": Record<string, never>;
  "materials:article:viewed": { articleId: string };
  "materials:video:play": { videoId: string };
  "materials:video:pause": { videoId: string };
  "materials:video:seek": { videoId: string };
  "action:opened": { stepId: string };
  "action:completed": { stepId: string };
  "videochat:active:speaker:started": { userId: string };
  "videochat:active:speaker:stopped": { userId: string };
  "videochat:error": { errorType: string };
  "videochat:reaction:sent": { emoji: string };
  "videochat:reaction:received": { emoji: string; senderAttendeeId: string };
};
